import React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { DocumentsDataTableQuery } from 'generated/resources/datatable.graphql'
import { DocumentsDocument } from 'generated/resources/graphql'

import { useDataTable } from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { downloadBlob } from 'modules/core/lib/download'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useBulkUpdateFolderDocument from 'modules/documents/hooks/documents/useBulkUpdateFolderDocument'
import useBulkUpdateVisibilityDocument from 'modules/documents/hooks/documents/useBulkUpdateVisibilityDocument'
import useMoveToTrashBinDocument from 'modules/documents/hooks/documents/useMoveToTrashBinDocument'
import {
  canUpdateDocuments,
  downloadDocuments,
  isCompanyInternalSpace,
  isCompanySpace,
  isPendingSignature,
} from 'modules/documents/lib/Documents'
import { DocumentStatuses } from 'modules/documents/lib/enums'
import { useBulkResendRequestSignature } from 'modules/esignatures/hooks/signatures/useBulkResendRequestSignature'

import HideModal from './HideModal'
import ResendSignatureReminderModal from './ResendSignatureReminderModal'
import TrashBinModal from './TrashBinModal'
import UpdateCompanyFolderModal from './UpdateCompanyFolderModal'
import UpdateFolderModal from './UpdateFolderModal'
import VisibleModal from './VisibleModal'

interface Props {
  space: string
}

const BulkActionsDatatable = ({ space }: Props) => {
  const session = useCurrentSession()
  const { data, selectedRows } = useDataTable<DocumentsDataTableQuery, DocumentsDocument>()
  const { moveDocumentToTrashBin, loading: isMovingToTrashBin } = useMoveToTrashBinDocument({
    fromDataTable: true,
  })
  const { bulkUpdateVisibility, loading: isUpdatingVisibilityDocuments } =
    useBulkUpdateVisibilityDocument({
      fromDataTable: true,
    })
  const { bulkResendRequestSignature, loading: isResendingSignatureReminder } =
    useBulkResendRequestSignature()
  const { bulkUpdateFolder } = useBulkUpdateFolderDocument({ fromDataTable: true })

  const selectedIds = [...selectedRows].map(Number)
  const selectedDocuments = data.filter(({ id }) => selectedIds.includes(id))
  const selectedDocumentsWithPendingSignature = selectedDocuments.filter((doc) => {
    return isPendingSignature(doc) && doc.status !== DocumentStatuses.ToFill
  })
  const selectedDocumentsWithSignature = selectedDocuments.filter((doc) => {
    return doc.signatureStatus !== null
  })
  const [isDownloading, toggleDownloading] = React.useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)
  const [hideModalOpen, setHideModalOpen] = React.useState(false)
  const [visibleModalOpen, setVisibleModalOpen] = React.useState(false)
  const [loadingUpdateFolder, setLoadingUpdateFolder] = React.useState(false)
  const [selectFolderModalOpen, setSelectFolderModalOpen] = React.useState(false)
  const [resendSignatureConfirmationModalOpen, setResendSignatureConfirmationModalOpen] =
    React.useState(false)

  const canUpdateSelectedDocuments = canUpdateDocuments(selectedDocuments)
  const canResendSignatureReminder = selectedDocumentsWithPendingSignature.length > 0
  const isInternalSpace = isCompanyInternalSpace(space)
  const isCompanySpaceVar = isCompanySpace(space)

  const onDownload = async () => {
    toggleDownloading(true)

    const response = await downloadDocuments(selectedIds)
    const blob = new Blob([response], { type: 'application/zip' })
    downloadBlob(blob, `${i18n.t('documents.employee.title')}.zip`)

    toggleDownloading(false)
  }

  const onMoveToTrashBin = async () => {
    const result = await moveDocumentToTrashBin({ documentIds: selectedIds })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }

    setDeleteModalOpen(false)
  }

  const isLoading =
    isDownloading ||
    isMovingToTrashBin ||
    isUpdatingVisibilityDocuments ||
    isResendingSignatureReminder

  if (!selectedDocuments.length) {
    return null
  }

  if (isLoading) {
    return (
      <Button iconName="spinner" type="secondary" loading>
        {i18n.t('common.actions')}
      </Button>
    )
  }

  const isMyDocument = (document: DocumentsDocument) => {
    return document.employee ? session.employee?.id === document.employee.id : false
  }

  const canDoVisibleSelectedDocuments = selectedDocuments.some((doc) => {
    return doc.public === false && !isMyDocument(doc)
  })

  const canDoInvisibleSelectedDocuments = selectedDocuments.some((doc) => {
    return doc.public === true && !isMyDocument(doc)
  })

  const onUpdateVisibility = async (visible: boolean, sendNotifications: boolean) => {
    const result = await bulkUpdateVisibility({
      documentIds: selectedIds,
      sendNotifications: sendNotifications,
      visibility: visible,
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }

    setDeleteModalOpen(false)
  }

  const onSubmitUpdateFolder = async (selectedFolderId: number | null) => {
    setLoadingUpdateFolder(true)
    // TODO: add functionality in backend to manage company folders
    const result = await bulkUpdateFolder({
      documentIds: selectedIds,
      folderId: selectedFolderId,
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('documents.employee.bulk-actions.update-folder.success'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }

    setLoadingUpdateFolder(false)
    setSelectFolderModalOpen(false)
  }

  const submitHideElements = () => {
    onUpdateVisibility(false, false)
    setHideModalOpen(false)
  }

  const submitVisibleElements = (sendNotifications: boolean) => {
    onUpdateVisibility(true, sendNotifications)
    setVisibleModalOpen(false)
  }

  const onSubmitResendSignatureReminder = async () => {
    const result = await bulkResendRequestSignature({
      documentIds: selectedDocumentsWithPendingSignature.map((doc) => doc.id),
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.employee.bulk-actions.resend-signature.success', {
          count: selectedIds.length,
        }),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }

    setResendSignatureConfirmationModalOpen(false)
  }

  return (
    <>
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <Button.Decorative type="secondary">{i18n.t('common.actions')}</Button.Decorative>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="end">
          {canUpdateSelectedDocuments && !isInternalSpace && (
            <PopoverMenu.TouchableOption
              title={i18n.t('documents.employee.bulk-actions.hide')}
              disabled={!canDoInvisibleSelectedDocuments}
              icon="eye-invisible"
              onClick={() => setHideModalOpen(true)}
            />
          )}
          {canUpdateSelectedDocuments && !isInternalSpace && (
            <PopoverMenu.TouchableOption
              title={i18n.t('documents.employee.bulk-actions.visible')}
              icon="eye-visible"
              disabled={!canDoVisibleSelectedDocuments}
              onClick={() => setVisibleModalOpen(true)}
            />
          )}
          {canResendSignatureReminder && (
            <PopoverMenu.TouchableOption
              icon="bell"
              onClick={() => setResendSignatureConfirmationModalOpen(true)}
              title={i18n.t('documents.employee.bulk-actions.resend_signature_reminder')}
            />
          )}
          {canUpdateSelectedDocuments && (
            <PopoverMenu.TouchableOption
              title={i18n.t('documents.move')}
              icon="folder"
              onClick={() => setSelectFolderModalOpen(true)}
            />
          )}
          <PopoverMenu.TouchableOption
            title={i18n.t('documents.employee.bulk-actions.download')}
            icon="download"
            onClick={onDownload}
          />
          {canUpdateSelectedDocuments && (
            <PopoverMenu.TouchableOption
              title={i18n.t('documents.move_to_trash_bin')}
              icon="delete"
              danger
              onClick={() => setDeleteModalOpen(true)}
            />
          )}
        </PopoverMenu.Content>
      </PopoverMenu>
      {deleteModalOpen && (
        <TrashBinModal
          filename={selectedDocuments[0]?.filename}
          totalDocuments={selectedDocuments.length}
          onClose={() => setDeleteModalOpen(false)}
          onSubmit={onMoveToTrashBin}
          loading={isMovingToTrashBin}
          hasSignature={selectedDocumentsWithSignature.length > 0}
        />
      )}
      {hideModalOpen && (
        <HideModal
          totalSelectedDocuments={selectedDocuments.length}
          loading={isUpdatingVisibilityDocuments}
          onSubmit={() => submitHideElements()}
          onClose={() => setHideModalOpen(false)}
        />
      )}
      {visibleModalOpen && (
        <VisibleModal
          totalSelectedDocuments={selectedDocuments.length}
          loading={isUpdatingVisibilityDocuments}
          onSubmit={submitVisibleElements}
          onClose={() => setVisibleModalOpen(false)}
        />
      )}
      {resendSignatureConfirmationModalOpen && (
        <ResendSignatureReminderModal
          totalSelectedDocuments={selectedDocumentsWithPendingSignature.length}
          onClose={() => setResendSignatureConfirmationModalOpen(false)}
          onSubmit={onSubmitResendSignatureReminder}
          loading={isResendingSignatureReminder}
        />
      )}
      {selectFolderModalOpen && selectedDocuments && !isCompanySpaceVar && (
        <UpdateFolderModal
          documents={selectedDocuments}
          onClose={() => setSelectFolderModalOpen(false)}
          onSubmit={onSubmitUpdateFolder}
          loading={loadingUpdateFolder}
        />
      )}
      {selectFolderModalOpen && selectedDocuments && isCompanySpaceVar && (
        <UpdateCompanyFolderModal
          documents={selectedDocuments}
          onClose={() => setSelectFolderModalOpen(false)}
          onSubmit={onSubmitUpdateFolder}
          space={space}
        />
      )}
    </>
  )
}

export default BulkActionsDatatable
